import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { getMyShifts, type MyShiftRow } from '../../api/assignments';
import { dinamicasDeCampania } from '../../api/impulso';
import {
  cargarConfigVisitasPuesto, misVisitasSoftysDelDia, UNIDADES_PEDIDO,
  type TipoVisitaSoftys, type UnidadPedido, type VisitaSoftysDelDia,
} from '../../api/softys';
import { SIN_DINAMICA, type ConfigVisitasPuesto } from '../../domain/softysConfig';
import {
  flushSoftys, guardarVisitaSoftys, pendingSoftysCount, visitasSoftysLocalesDe,
} from '../../offline/softysQueue';
import { compressImage } from '../../lib/compressImage';
import CampoFoto from '../../components/CampoFoto';
import { getGpsFix, type GpsFix } from './Marcar';

const LIMA = 'America/Lima';

/**
 * Softys en mercados (Toallitas Húmedas desde el 14-set, Ladysoft desde el 27-oct) — registrar UN
 * puesto del mercado. Lo que cambia entre campañas (premios, regla de compra, preguntas extra)
 * sale de `campaigns.visitas_puesto_config`, no de este archivo.
 *
 * La misma pantalla sirve a los dos instrumentos y el botón que la abrió decide cuál:
 * `/movil/visita-puesto/comerciante` y `/movil/visita-puesto/venta`. Comparten la cabecera —qué
 * puesto es, el DNI del comerciante y la foto de la fachada— y cambian abajo.
 *
 * **Está pensada para llenarse treinta veces seguidas.** Por eso, al guardar, no navega a ningún
 * lado: limpia lo del puesto, deja el foco arriba y muestra el contador del día. Volver al menú
 * después de cada registro serían sesenta toques extra por jornada, de pie en un pasillo.
 *
 * **Todo pasa por la cola local, incluso con señal.** Un solo camino tiene un solo conjunto de
 * errores posibles, y es el mismo camino que ya funciona sin conexión — que acá no es un detalle:
 * el 14-set los mercados son Iquitos, Juliaca, Tacna, Andahuaylas, Tumbes y Huánuco.
 */
export default function VisitaPuesto() {
  const { tipo: tipoParam } = useParams<{ tipo: string }>();
  const tipo: TipoVisitaSoftys = tipoParam === 'venta' ? 'venta' : 'comerciante';
  const esVenta = tipo === 'venta';

  const hoyLima = useMemo(() => formatInTimeZone(new Date(), LIMA, 'yyyy-MM-dd'), []);
  /**
   * EL DÍA DEL REGISTRO SE ELIGE (pedido del usuario, 02-nov, Ladysoft).
   *
   * Abre en hoy, pero se puede cambiar a un día anterior: lo que se anotó en papel, o lo que no se
   * alcanzó a cargar en el mercado, se carga después **en el día en que pasó**. El turno y el
   * mercado salen de ESE día, y el tablero lo cuenta en esa fecha (`softys_calendario` agrupa por
   * `fecha`, no por la hora de carga). No se permite un día futuro: lo que no pasó no se registra.
   */
  const [fecha, setFecha] = useState(hoyLima);
  const esOtraFecha = fecha !== hoyLima;

  const [turnos, setTurnos] = useState<MyShiftRow[]>([]);
  const [turnoElegido, setTurnoElegido] = useState<string | null>(null);
  /** La configuración de cada campaña de los turnos del día, por id de campaña. */
  const [configs, setConfigs] = useState<Record<string, ConfigVisitasPuesto>>({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [pendientes, setPendientes] = useState(0);
  const [registrados, setRegistrados] = useState(0);

  /**
   * 🔴 LA UBICACIÓN, QUE HASTA EL 17-SET NO SE PEDÍA.
   *
   * Esta pantalla mandaba `lat: null, lng: null` **cableado**: no es que el GPS fallara, es que
   * nunca se le preguntó al teléfono. Las 549 visitas de los tres primeros días de campaña —de
   * Iquitos a Tacna— quedaron sin una sola coordenada, y como las banderas tampoco se llenaban,
   * en la bandeja no se veía ningún hueco. El dato faltante era invisible.
   *
   * Se pide UNA vez al abrir y **se refresca después de cada registro**, no antes. Dos razones:
   * quien llena esta pantalla recorre treinta puestos del mismo mercado, así que un solo fix al
   * montar sellaría los treinta con la misma coordenada —que es una precisión inventada—; y pedirlo
   * al guardar agregaría una espera justo en el momento en que la persona quiere pasar al
   * siguiente puesto.
   *
   * ⚠️ **No bloquea nada, y eso no es negociable** (regla del usuario, 14-set): si no hay fix, la
   * visita se guarda igual con `sin_gps`. Esperar la ubicación no protegería ningún dato —la
   * coordenada siempre fue opcional— y sí impediría trabajar.
   */
  const [gps, setGps] = useState<GpsFix | null>(null);

  const pedirUbicacion = useCallback(() => {
    void getGpsFix().then(({ fix }) => setGps(fix));
  }, []);

  useEffect(() => { pedirUbicacion(); }, [pedirUbicacion]);

  // --- lo que se llena por puesto ------------------------------------------
  const [puestoNumero, setPuestoNumero] = useState('');
  const [puestoNombre, setPuestoNombre] = useState('');
  const [comercianteDni, setComercianteDni] = useState('');
  const [fachada, setFachada] = useState<Blob | null>(null);
  const [premio, setPremio] = useState('');
  // comerciante
  const [hizoPedido, setHizoPedido] = useState<boolean | null>(null);
  const [pedidoUnidad, setPedidoUnidad] = useState<UnidadPedido | ''>('');
  const [pedidoCantidad, setPedidoCantidad] = useState('');
  const [pedidoFoto, setPedidoFoto] = useState<Blob | null>(null);
  const [escucho, setEscucho] = useState<boolean | null>(null);
  const [salidaFoto, setSalidaFoto] = useState<Blob | null>(null);
  // comerciante, solo si la campaña lo pregunta (Ladysoft)
  const [muestra, setMuestra] = useState<boolean | null>(null);
  const [bonificacion, setBonificacion] = useState('');
  const [dinamica, setDinamica] = useState('');
  // venta
  const [promociones, setPromociones] = useState('');
  /** Lo ya registrado hoy: servidor + lo que espera señal, lo más nuevo arriba. */
  const [lista, setLista] = useState<VisitaSoftysDelDia[]>([]);
  const [verLista, setVerLista] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const filas = await getMyShifts(fecha);
      /**
       * ⚠️ NO se toma `filas[0]`. Es el patrón que este proyecto tiene anotado como el error más
       * caro —«tomar el primero de una lista y decidir por todo el conjunto»—: con dos turnos el
       * mismo día, el primero mandaría las visitas al mercado equivocado sin un solo aviso.
       * Se filtran las campañas que de verdad recorren puestos, y si queda más de una, elige ella.
       */
      const conVisitas: MyShiftRow[] = [];
      for (const f of filas) {
        const d = await dinamicasDeCampania(f.position.campaign_id);
        if (d.visitasPuesto) conVisitas.push(f);
      }
      // Una lectura por campaña, no por turno. Si falla, lanza: es preferible «no se pudo cargar»
      // a una pantalla con los premios de otra campaña.
      const porCampania: Record<string, ConfigVisitasPuesto> = {};
      for (const f of conVisitas) {
        const id = f.position.campaign_id;
        if (!(id in porCampania)) porCampania[id] = await cargarConfigVisitasPuesto(id);
      }
      setConfigs(porCampania);
      setTurnos(conVisitas);
      // Al cambiar de fecha cambian los turnos: el elegido solo se conserva si sigue en la lista.
      setTurnoElegido((previo) => (conVisitas.some((t) => t.id === previo)
        ? previo
        : (conVisitas.length === 1 ? conVisitas[0].id : null)));

      await cargarLoRegistrado();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar tu turno.');
    } finally {
      setCargando(false);
    }
  }, [fecha, tipo]);

  useEffect(() => { void recargar(); }, [recargar]);

  /**
   * 🔴 LO REGISTRADO SE CUENTA DEL SERVIDOR **Y** DE LA COLA, NO SOLO DE LA COLA.
   *
   * Acá decía `visitasSoftysLocalesDe(fecha).length`, o sea que contaba únicamente lo que seguía
   * esperando señal. Cuando una visita se subía bien, `flushSoftys` la borraba de la cola y el
   * contador volvía a cero: **mientras mejor funcionaba, más decía que no funcionaba.**
   *
   * El 14-set eso produjo tres reportes de campo el mismo día —«registré varios y no me sale»—,
   * gente registrando de nuevo puestos que ya estaban guardados, y una mañana entera de
   * desconfianza en un sistema que estaba andando bien. Un contador que miente hacia abajo es peor
   * que no tener contador: empuja a duplicar el trabajo.
   */
  const cargarLoRegistrado = useCallback(async () => {
    let delServidor: VisitaSoftysDelDia[] = [];
    try {
      delServidor = await misVisitasSoftysDelDia(fecha);
    } catch {
      // Sin señal no se puede leer el servidor. No es un error de la pantalla: abajo se suma
      // igual lo que hay en el teléfono, que es justo lo que importa en ese caso.
    }

    let locales: VisitaSoftysDelDia[] = [];
    try {
      const enCola = await visitasSoftysLocalesDe(fecha);
      locales = enCola.map((l) => ({
        id: l.clientUuid,
        tipo: l.input.tipo,
        puestoNumero: l.input.puestoNumero ?? null,
        puestoNombre: l.input.puestoNombre ?? null,
        premio: l.input.premio ?? null,
        ocurridoEn: l.input.ocurridoEn,
        comercianteDni: l.input.comercianteDni ?? null,
        hizoPedido: l.input.hizoPedido ?? null,
        pedidoUnidad: l.input.pedidoUnidad ?? null,
        pedidoCantidad: l.input.pedidoCantidad ?? null,
        escuchoCapacitacion: l.input.escuchoCapacitacion ?? null,
        muestraEntregada: l.input.muestraEntregada ?? null,
        bonificacionPacks: l.input.bonificacionPacks ?? null,
        dinamicaMayorista: l.input.dinamicaMayorista ?? null,
        promociones: l.input.promociones ?? null,
        tieneFotoFachada: l.fachadaBlob !== null,
        horaReal: l.input.horaReal ?? true,
        pendiente: true,
      }));
    } catch {
      // La base local del navegador puede estar caída; lo del servidor se muestra igual.
    }

    const todo = [...delServidor, ...locales]
      .sort((a, b) => b.ocurridoEn.localeCompare(a.ocurridoEn));
    setLista(todo);
    setRegistrados(todo.length);
  }, [fecha]);

  const revisarCola = useCallback(async () => {
    // Todo lo de la cola local va dentro del try, contador incluido: si la base local no responde
    // —otra pestaña bloqueando el esquema, almacenamiento lleno—, esta pantalla tiene que seguir
    // funcionando. Lo guardado no se pierde por no poder contarlo.
    try {
      await flushSoftys();
      setPendientes(await pendingSoftysCount());
    } catch {
      setPendientes(0);
    }
  }, []);

  useEffect(() => { void revisarCola(); }, [revisarCola]);

  const turno = turnos.find((t) => t.id === turnoElegido) ?? null;
  /** `null` mientras no eligió mercado: con dos turnos de campañas distintas no hay una sola
   *  lista de premios que mostrar, y adivinar por el primero es el error más caro del proyecto. */
  const cfg: ConfigVisitasPuesto | null = turno ? configs[turno.position.campaign_id] ?? null : null;

  // Al cambiar de mercado, un premio que la otra campaña no tiene no puede quedar elegido.
  useEffect(() => {
    if (cfg !== null && premio !== '' && !cfg.premios.includes(premio)) setPremio('');
  }, [cfg, premio]);

  /** Comprime al capturar, no al subir: una jornada de treinta puestos con fotos sin comprimir
   *  llenaría el almacenamiento del navegador antes del mediodía. Si la compresión falla se usa
   *  la original: no poder comprimir nunca debe impedir registrar. */
  async function prepararFoto(e: ChangeEvent<HTMLInputElement>): Promise<Blob | null> {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return null;
    try {
      return await compressImage(file);
    } catch {
      return file;
    }
  }

  function limpiar(): void {
    setPuestoNumero('');
    setPuestoNombre('');
    setComercianteDni('');
    setFachada(null);
    setPremio('');
    setHizoPedido(null);
    setPedidoUnidad('');
    setPedidoCantidad('');
    setPedidoFoto(null);
    setEscucho(null);
    setSalidaFoto(null);
    setMuestra(null);
    setBonificacion('');
    setDinamica('');
    setPromociones('');
  }

  /** Qué falta para poder guardar. `null` = está listo. Devuelve UNA cosa, la primera: una lista
   *  de seis errores en un celular es una pantalla que nadie lee. */
  const falta: string | null = (() => {
    if (!turno) return 'Elige el turno del mercado donde estás.';
    if (puestoNumero.trim() === '') return 'Falta el número del puesto.';
    if (!fachada) return 'Falta la foto de la fachada del puesto.';
    if (esVenta && promociones.trim() === '') return 'Falta cuántas promociones compró.';
    if (!esVenta && hizoPedido === null) return 'Falta decir si hizo pedido.';
    if (!esVenta && hizoPedido === true && pedidoUnidad === '') {
      return 'Falta decir si el pedido es en planchas o en unidades.';
    }
    if (!esVenta && hizoPedido === true && pedidoCantidad.trim() === '') {
      return 'Falta cuántas planchas o unidades pidió.';
    }
    // Siempre hay una respuesta posible («Sin dinámica»), así que pedirla no traba a nadie: lo que se
    // quiere medir es justamente con qué dinámica se cerró cada pedido.
    if (!esVenta && hizoPedido === true && (cfg?.dinamicasMayorista.length ?? 0) > 0 && dinamica === '') {
      return 'Falta decir con qué dinámica compró.';
    }
    return null;
  })();

  /**
   * Si la persona ya empezo a llenar ESTE puesto.
   *
   * Existe por un reporte de campo del 14-set: *«hice un registro, me sale que se registro, pero
   * abajo me sale un error en letras rojas que falta el codigo»*. No era un error del guardado. Al
   * guardar, la pantalla se limpia para el puesto siguiente —esta pensada para llenarse treinta
   * veces seguidas— y el aviso de «falta el numero del puesto» se refiere al que todavia no
   * empezo. Pero aparece pegado al «guardado» del anterior, y cualquiera lee que algo fallo.
   *
   * Un aviso correcto en el momento equivocado se lee como un error. Ahora el aviso espera a que
   * la persona toque algo.
   */
  const tocado = puestoNumero.trim() !== '' || puestoNombre.trim() !== ''
    || comercianteDni.trim() !== '' || fachada !== null || premio !== ''
    || hizoPedido !== null || pedidoUnidad !== '' || pedidoCantidad.trim() !== ''
    || pedidoFoto !== null || escucho !== null || salidaFoto !== null
    || muestra !== null || bonificacion.trim() !== '' || dinamica !== ''
    || promociones.trim() !== '';

  async function guardar(): Promise<void> {
    if (falta !== null || !turno || !cfg) { setError(falta ?? ''); return; }
    setGuardando(true);
    setError('');
    try {
      // Un solo uuid para la clave de la cola y para el envío: es lo que hace idempotente el
      // reintento. Si los dos pudieran divergir, un reenvío crearía un puesto duplicado — y un
      // puesto duplicado es una visita contada dos veces en el reporte del cliente.
      const clientUuid = crypto.randomUUID();
      const comoSeGuardo = await guardarVisitaSoftys({
        clientUuid,
        input: {
          clientUuid,
          tipo,
          contexto: {
            assignmentId: turno.id,
            pdvId: turno.position.pdv.id,
            fecha,
          },
          // El «horario de la visita» del cuestionario: se sella solo. Una hora tecleada treinta
          // veces por jornada sale mal la mitad de las veces.
          //
          // Para OTRA fecha no hay hora que sellar: se pone el mediodía de ese día en Lima y se
          // marca `horaReal: false`. Sellar la hora de carga pondría la visita en el día de hoy en
          // cualquier lectura por hora, y sería una hora inventada que se lee como medida.
          ocurridoEn: esOtraFecha
            ? new Date(`${fecha}T12:00:00-05:00`).toISOString()
            : new Date().toISOString(),
          ...(esOtraFecha ? { horaReal: false } : {}),
          puestoNumero,
          puestoNombre,
          comercianteDni: comercianteDni.trim() || null,
          // Cargado después, la coordenada de AHORA es la de la casa o la oficina, no la del puesto:
          // no viaja. Se dice con su propia bandera, no con `sin_gps`, que significa otra cosa.
          lat: esOtraFecha ? null : gps?.lat ?? null,
          lng: esOtraFecha ? null : gps?.lng ?? null,
          accuracy: esOtraFecha ? null : gps?.accuracy ?? null,
          // Lo que no se pudo medir se DICE. Una fila sin coordenada y sin bandera se lee igual
          // que una con coordenada en cualquier conteo que no mire columna por columna.
          flags: esOtraFecha ? ['otra_fecha'] : (gps === null ? ['sin_gps'] : []),
          premio: premio === '' ? null : premio,
          ...(esVenta
            ? { promociones: Number(promociones) || null }
            : {
              hizoPedido,
              // Si contesto que NO hizo pedido, la unidad y la cantidad NO viajan.
              //
              // Los campos se ocultan al contestar «No», pero su valor se queda en memoria: quien
              // marca «Sí», elige «5 planchas» y despues corrige a «No», dejaba una fila que dice
              // que no pidio nada Y ADEMAS que pidio 5 planchas. Se vio en campo el 14-set.
              // Lo que no se ve en pantalla no puede seguir viajando al servidor.
              pedidoUnidad: hizoPedido === true && pedidoUnidad !== '' ? pedidoUnidad : null,
              pedidoCantidad: hizoPedido === true && pedidoCantidad.trim() !== ''
                ? Number(pedidoCantidad)
                : null,
              escuchoCapacitacion: escucho,
              // Lo que la campaña no pregunta NO viaja (undefined), para que en `ficha` no se
              // confunda «no se preguntó» con «se preguntó y quedó en blanco».
              ...(cfg.preguntaMuestra ? { muestraEntregada: muestra } : {}),
              // Igual que la cantidad del pedido: si contestó que no pidió, no hay bonificación.
              ...(cfg.preguntaBonificacion
                ? {
                  bonificacionPacks: hizoPedido === true && bonificacion.trim() !== ''
                    ? Number(bonificacion)
                    : null,
                }
                : {}),
              ...(cfg.dinamicasMayorista.length > 0
                ? { dinamicaMayorista: hizoPedido === true && dinamica !== '' ? dinamica : null }
                : {}),
            }),
        },
        fachadaBlob: fachada,
        pedidoBlob: esVenta ? null : pedidoFoto,
        salidaBlob: esVenta ? null : salidaFoto,
      });

      const numero = puestoNumero;
      limpiar();
      setRegistrados((n) => n + 1);
      // Se distingue a proposito. «Enviado» sin respaldo local significa que este telefono no
      // pudo usar su memoria: si despues se corta la senal, lo siguiente NO va a quedar esperando
      // como la persona espera. Decirle «guardado» a secas la dejaria confiada en una red que en
      // ese telefono ya no la esta cubriendo.
      setAviso(comoSeGuardo === 'enviada_directo'
        ? `Puesto ${numero} enviado. Ojo: este telefono no pudo guardar respaldo local, asi que `
          + 'conviene recargar la pagina cuando puedas.'
        : `Puesto ${numero} guardado.`);
      await revisarCola();
      await cargarLoRegistrado();
      // La ubicación del SIGUIENTE puesto se pide ahora, mientras la persona camina hasta él. Al
      // guardar ya sería tarde: o se espera —y son treinta esperas por jornada— o se sella con la
      // coordenada del puesto anterior.
      pedirUbicacion();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) {
    return <p className="p-4 text-center text-gray-600">Cargando tu turno…</p>;
  }

  /** Al cambiar el día se va a cargar otra lista de turnos: el aviso del puesto anterior ya no
   *  corresponde. Lo llenado del puesto se conserva, por si la fecha se corrigió a medio llenar. */
  function cambiarFecha(f: string): void {
    setAviso('');
    setError('');
    setFecha(f);
  }

  const campoFecha = <CampoFecha fecha={fecha} max={hoyLima} onCambiar={cambiarFecha} />;
  const elDia = esOtraFecha ? `el ${diaMes(fecha)}` : 'hoy';

  if (turnos.length === 0) {
    return (
      <div className="space-y-3 p-4">
        {/* Sin turno ese día también se puede cambiar la fecha: es justo lo que hace falta cuando
            se eligió un día equivocado. */}
        {campoFecha}
        {/* Si la carga FALLÓ, se dice eso: «no tienes turno» sobre un error de red o de
            configuración manda a la persona a reclamar un turno que sí tiene. */}
        {error !== '' ? (
          <p role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
            {error}
            <button type="button" onClick={() => void recargar()}
              className="mt-2 block font-medium text-red-900 underline">
              Volver a cargar
            </button>
          </p>
        ) : (
          <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            {esOtraFecha ? `El ${diaMes(fecha)}` : 'Hoy'} no tienes un turno de esta campaña. Si
            crees que es un error, avisa a tu coordinador.
          </p>
        )}
        <Link to="/movil" className="block text-center text-sm text-blue-700 underline">
          Volver a mi turno
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 pb-24">
      <header>
        <h1 className="text-xl font-bold text-gray-900">
          {esVenta
            ? (cfg?.rotuloVenta ?? 'Visita de venta')
            : (cfg?.rotuloComerciante ?? 'Visita a comerciante')}
        </h1>
        {turno && (
          <p className="text-sm text-gray-600">{turno.position.pdv.name}</p>
        )}
        <div className="mt-3">{campoFecha}</div>
        {esOtraFecha && (
          <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            Estás registrando puestos del <b>{diaMes(fecha)}</b>. Se guardan con la hora como
            aproximada y sin ubicación.
          </p>
        )}
        <p className="mt-2 text-sm text-gray-700">
          Llevas <b>{registrados}</b> {registrados === 1 ? 'puesto registrado' : 'puestos registrados'} {elDia}
          {pendientes > 0 && <> · <span className="text-amber-700">{pendientes} esperando señal</span></>}
        </p>
      </header>

      {/*
        LO QUE YA REGISTRÓ, ACÁ MISMO Y NO EN OTRA PANTALLA.

        Pedido del usuario el 14-set, con tres reportes de campo el mismo día detrás: *«que los
        auditores puedan ver los registros que van haciendo […] siempre primero la última visita, y
        al darle click se abra el detalle para que puedan validar lo que subieron»*.

        Va plegado: quien está registrando el puesto 12 no necesita los once anteriores empujándole
        el formulario hacia abajo. Pero está a un toque, que es lo que hace falta cuando alguien
        duda de si lo suyo entró.
      */}
      {lista.length > 0 && (
        <section className="rounded-lg border border-gray-200 bg-white">
          <button
            type="button"
            onClick={() => setVerLista((v) => !v)}
            className="flex w-full items-center justify-between px-3 py-3 text-left text-sm font-medium text-blue-700"
          >
            <span>{verLista ? 'Ocultar lo registrado' : 'Ver lo que llevas registrado'}</span>
            <span aria-hidden="true">{verLista ? '\u25b2' : '\u25bc'}</span>
          </button>

          {verLista && (
            <ul className="divide-y divide-gray-100 border-t border-gray-100">
              {lista.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setAbierto((a) => (a === r.id ? null : r.id))}
                    aria-expanded={abierto === r.id}
                    className="flex w-full items-start justify-between gap-2 px-3 py-3 text-left"
                  >
                    <span className="text-sm text-gray-900">
                      <b>{r.puestoNumero ?? 'sin n\u00famero'}</b>
                      {r.puestoNombre ? ` \u00b7 ${r.puestoNombre}` : ''}
                      <span className="block text-xs font-normal text-gray-500">
                        {r.horaReal === false ? 'cargado despu\u00e9s' : hhmm(r.ocurridoEn)}
                        {' \u00b7 '}{r.tipo === 'venta' ? 'venta' : 'comerciante'}
                        {r.pendiente === true && (
                          <span className="text-amber-700"> \u00b7 esperando se\u00f1al</span>
                        )}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-blue-700">
                      {abierto === r.id ? 'cerrar' : 'ver'}
                    </span>
                  </button>

                  {abierto === r.id && (
                    <dl className="grid grid-cols-2 gap-x-3 gap-y-2 px-3 pb-4 text-sm">
                      <Dato rotulo="DNI del comerciante" valor={r.comercianteDni ?? '\u2014'} />
                      <Dato
                        rotulo="Foto de la fachada"
                        valor={r.tieneFotoFachada ? 'tomada' : 'sin foto'}
                      />
                      {r.tipo === 'venta' ? (
                        <Dato
                          rotulo="Promociones"
                          valor={r.promociones === null ? '\u2014' : String(r.promociones)}
                        />
                      ) : (
                        <>
                          <Dato
                            rotulo="\u00bfHizo pedido?"
                            valor={r.hizoPedido === null ? '\u2014' : (r.hizoPedido ? 'S\u00ed' : 'No')}
                          />
                          <Dato
                            rotulo="Pedido"
                            valor={r.pedidoCantidad === null
                              ? '\u2014'
                              : `${r.pedidoCantidad} ${r.pedidoUnidad ?? ''}`.trim()}
                          />
                          <Dato
                            rotulo="\u00bfEscuch\u00f3 la capacitaci\u00f3n?"
                            valor={r.escuchoCapacitacion === null
                              ? '\u2014'
                              : (r.escuchoCapacitacion ? 'S\u00ed' : 'No')}
                          />
                          {(cfg?.preguntaMuestra === true || r.muestraEntregada !== null) && (
                            <Dato
                              rotulo="\u00bfRecibi\u00f3 la muestra?"
                              valor={r.muestraEntregada === null
                                ? '\u2014'
                                : (r.muestraEntregada ? 'S\u00ed' : 'No')}
                            />
                          )}
                          {r.dinamicaMayorista !== null && (
                            <Dato rotulo="Dinámica" valor={r.dinamicaMayorista} />
                          )}
                          {(cfg?.preguntaBonificacion === true || r.bonificacionPacks !== null) && (
                            <Dato
                              rotulo="Packs de bonificaci\u00f3n"
                              valor={r.bonificacionPacks === null ? '\u2014' : String(r.bonificacionPacks)}
                            />
                          )}
                        </>
                      )}
                      <Dato rotulo="Premio de la ruleta" valor={r.premio ?? 'no gir\u00f3'} />
                    </dl>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Con más de un turno del mismo día, elige ella. Nunca se decide por el primero. */}
      {turnos.length > 1 && (
        <label className="block text-sm">
          ¿En qué mercado estás?
          <select
            className="mt-1 w-full rounded border px-3 py-3 text-base"
            value={turnoElegido ?? ''}
            onChange={(e) => setTurnoElegido(e.target.value || null)}
          >
            <option value="">Elige…</option>
            {turnos.map((t) => (
              <option key={t.id} value={t.id}>{t.position.pdv.name}</option>
            ))}
          </select>
        </label>
      )}

      {aviso !== '' && (
        <p role="status" className="rounded-lg border border-green-300 bg-green-50 p-3 text-sm text-green-900">
          {aviso}
        </p>
      )}
      {error !== '' && (
        <p role="alert" className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          {error}
        </p>
      )}

      <label className="block text-sm">
        Número del puesto
        <span className="block text-xs text-gray-500">
          El que tiene dentro del mercado. Se escribe tal cual: «12B», «A-4».
        </span>
        <input
          className="mt-1 w-full rounded border px-3 py-3 text-base"
          value={puestoNumero}
          onChange={(e) => setPuestoNumero(e.target.value)}
        />
      </label>

      <label className="block text-sm">
        Nombre del puesto
        <span className="block text-xs text-gray-500">Tal cual está escrito en la tienda.</span>
        <input
          className="mt-1 w-full rounded border px-3 py-3 text-base"
          value={puestoNombre}
          onChange={(e) => setPuestoNombre(e.target.value)}
        />
      </label>

      <label className="block text-sm">
        DNI del comerciante <span className="text-gray-500">(opcional)</span>
        <input
          inputMode="numeric"
          className="mt-1 w-full rounded border px-3 py-3 text-base"
          value={comercianteDni}
          onChange={(e) => setComercianteDni(e.target.value)}
        />
      </label>

      <div>
        <CampoFoto
          etiqueta="Foto de la fachada del puesto"
          nombre="la fachada del puesto"
          lista={fachada !== null}
          listaTexto="Foto lista"
          onElegir={async (e) => setFachada(await prepararFoto(e))}
        />
        {fachada !== null && <p className="mt-1 text-xs text-green-700">Foto lista.</p>}
      </div>

      {esVenta ? (
        <label className="block text-sm">
          ¿Cuántas promociones compró?
          {cfg !== null && (
            <span className="block text-xs text-gray-500">{cfg.textoVenta}</span>
          )}
          <input
            inputMode="numeric"
            className="mt-1 w-full rounded border px-3 py-3 text-base"
            value={promociones}
            onChange={(e) => setPromociones(e.target.value)}
          />
        </label>
      ) : (
        <>
          <fieldset className="text-sm">
            <legend>¿Hizo pedido{cfg !== null ? ` de ${cfg.productoPedido}` : ''}?</legend>
            <div className="mt-1 flex gap-2">
              {[{ v: true, t: 'Sí' }, { v: false, t: 'No' }].map((o) => (
                <button
                  key={o.t}
                  type="button"
                  aria-pressed={hizoPedido === o.v}
                  onClick={() => setHizoPedido(o.v)}
                  className={`min-h-[44px] flex-1 rounded border py-2 ${
                    hizoPedido === o.v ? 'border-blue-600 bg-blue-50 font-medium' : 'border-gray-300'
                  }`}
                >
                  {o.t}
                </button>
              ))}
            </div>
          </fieldset>

          {hizoPedido === true && (
            <>
              <fieldset className="text-sm">
                <legend>¿Planchas o unidades?</legend>
                <div className="mt-1 flex gap-2">
                  {UNIDADES_PEDIDO.map((u) => (
                    <button
                      key={u}
                      type="button"
                      aria-pressed={pedidoUnidad === u}
                      onClick={() => setPedidoUnidad(u)}
                      className={`min-h-[44px] flex-1 rounded border py-2 capitalize ${
                        pedidoUnidad === u ? 'border-blue-600 bg-blue-50 font-medium' : 'border-gray-300'
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </fieldset>

              <label className="block text-sm">
                ¿Cuántas?
                <input
                  inputMode="numeric"
                  className="mt-1 w-full rounded border px-3 py-3 text-base"
                  value={pedidoCantidad}
                  onChange={(e) => setPedidoCantidad(e.target.value)}
                />
              </label>

              <div>
                <CampoFoto
                  etiqueta="Foto del pedido (opcional)"
                  nombre="el pedido"
                  lista={pedidoFoto !== null}
                  listaTexto="Foto lista"
                  onElegir={async (e) => setPedidoFoto(await prepararFoto(e))}
                />
                {pedidoFoto !== null && <p className="mt-1 text-xs text-green-700">Foto lista.</p>}
              </div>

              {cfg !== null && cfg.dinamicasMayorista.length > 0 && (
                <fieldset className="text-sm">
                  <legend>¿Con qué dinámica compró?</legend>
                  <div className="mt-1 grid gap-2">
                    {[...cfg.dinamicasMayorista, SIN_DINAMICA].map((d) => (
                      <button
                        key={d}
                        type="button"
                        aria-pressed={dinamica === d}
                        onClick={() => setDinamica(d)}
                        className={`min-h-[44px] rounded border px-3 py-2 text-left ${
                          dinamica === d ? 'border-blue-600 bg-blue-50 font-medium' : 'border-gray-300'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </fieldset>
              )}

              {cfg?.preguntaBonificacion === true && (
                <label className="block text-sm">
                  ¿Cuántos packs le entregaste de bonificación?
                  <span className="block text-xs text-gray-500">
                    Los packs de regalo que le diste por este pedido. Si no le diste nada, pon 0.
                  </span>
                  <input
                    inputMode="numeric"
                    className="mt-1 w-full rounded border px-3 py-3 text-base"
                    value={bonificacion}
                    onChange={(e) => setBonificacion(e.target.value)}
                  />
                </label>
              )}
            </>
          )}
        </>
      )}

      <fieldset className="text-sm">
        <legend>
          ¿Qué premio de la ruleta entregaste?{' '}
          <span className="text-gray-500">(si no giró, déjalo sin marcar)</span>
        </legend>
        {cfg === null && (
          <p className="mt-1 text-xs text-gray-500">Elige primero el mercado para ver los premios.</p>
        )}
        <div className="mt-1 grid grid-cols-2 gap-2">
          {(cfg?.premios ?? []).map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={premio === p}
              onClick={() => setPremio(premio === p ? '' : p)}
              className={`min-h-[44px] rounded border px-2 py-2 text-sm ${
                premio === p ? 'border-blue-600 bg-blue-50 font-medium' : 'border-gray-300'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </fieldset>

      {!esVenta && (
        <>
          <fieldset className="text-sm">
            <legend>¿Escuchó la capacitación?</legend>
            <div className="mt-1 flex gap-2">
              {[{ v: true, t: 'Sí' }, { v: false, t: 'No' }].map((o) => (
                <button
                  key={o.t}
                  type="button"
                  aria-pressed={escucho === o.v}
                  onClick={() => setEscucho(o.v)}
                  className={`min-h-[44px] flex-1 rounded border py-2 ${
                    escucho === o.v ? 'border-blue-600 bg-blue-50 font-medium' : 'border-gray-300'
                  }`}
                >
                  {o.t}
                </button>
              ))}
            </div>
          </fieldset>

          {cfg?.preguntaMuestra === true && (
            <fieldset className="text-sm">
              <legend>¿Le entregaste la muestra?</legend>
              <div className="mt-1 flex gap-2">
                {[{ v: true, t: 'Sí' }, { v: false, t: 'No' }].map((o) => (
                  <button
                    key={o.t}
                    type="button"
                    aria-pressed={muestra === o.v}
                    onClick={() => setMuestra(o.v)}
                    className={`min-h-[44px] flex-1 rounded border py-2 ${
                      muestra === o.v ? 'border-blue-600 bg-blue-50 font-medium' : 'border-gray-300'
                    }`}
                  >
                    {o.t}
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          <div>
            <CampoFoto
              etiqueta="Foto de salida"
              nombre="la salida"
              lista={salidaFoto !== null}
              listaTexto="Foto lista"
              onElegir={async (e) => setSalidaFoto(await prepararFoto(e))}
            />
            {salidaFoto !== null && <p className="mt-1 text-xs text-green-700">Foto lista.</p>}
          </div>
        </>
      )}

      {/* El motivo va PEGADO al botón, no arriba de la pantalla. Este proyecto ya tuvo tres casos
          reportados como «no pasa nada» que eran un mensaje fuera de la vista. */}
      {tocado && falta !== null && <p className="text-sm text-amber-800">{falta}</p>}

      <button
        type="button"
        onClick={() => void guardar()}
        disabled={falta !== null || guardando}
        className="w-full rounded-xl bg-emerald-700 py-4 text-lg font-bold text-white disabled:bg-gray-400"
      >
        {guardando ? 'Guardando…' : 'Guardar y seguir con el próximo'}
      </button>

      <Link to="/movil" className="block text-center text-sm text-blue-700 underline">
        Terminé, volver a mi turno
      </Link>
    </div>
  );
}

/** Hora de Lima de un sello ISO. */
function hhmm(iso: string): string {
  return formatInTimeZone(new Date(iso), LIMA, 'HH:mm');
}

/** «dd/mm» de una fecha `yyyy-MM-dd`, sin pasar por `Date`: medianoche en UTC ya es el día
 *  anterior en Lima. */
function diaMes(fecha: string): string {
  const [, m, d] = fecha.split('-');
  return `${d}/${m}`;
}

/** El día del registro. `max` es hoy: no se registra lo que todavía no pasó. Un valor vacío (el
 *  teléfono deja borrar el campo) o futuro se ignora, y queda la fecha que había. */
function CampoFecha({ fecha, max, onCambiar }: {
  fecha: string; max: string; onCambiar: (f: string) => void;
}) {
  return (
    <label className="block text-sm text-gray-700">
      Fecha del registro
      <input
        type="date"
        value={fecha}
        max={max}
        onChange={(e) => {
          const f = e.target.value;
          if (f !== '' && f <= max) onCambiar(f);
        }}
        className="mt-1 w-full rounded border px-3 py-3 text-base"
      />
    </label>
  );
}

/** Un dato del detalle. Rotulo arriba, valor abajo: en un celular angosto, dos columnas de
 *  \u00abr\u00f3tulo: valor\u00bb se parten por la mitad y se leen peor que una sola. */
function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-xs text-gray-500">{rotulo}</dt>
      <dd className="text-gray-900">{valor}</dd>
    </div>
  );
}
